import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import {
  Form,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Badge,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "views/pages/components/HusariaHeader";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

const VoicemailDetail = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const { id } = useParams();
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const [search, setSearchTerm] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]); // NEW: selected full_paths
  const [deleting, setDeleting] = useState(false); // NEW: delete-in-flight flag
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const visibleColumns = [
    { key: "created_date", label: "Received" },
    { key: "caller_id_name", label: "Caller ID Name" },
    { key: "caller_id_number", label: "Caller ID Number" },
    { key: "message_length", label: "Length" },
    { key: "file_size", label: "Size" },
    { key: "voicemail_description", label: "Description" },
  ];
  const fetchMessages = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${serverHost}/voicemails/${id}`);
      if (!response.ok) throw new Error("Failed to fetch voicemail messages.");

      const data = await response.json();
      const results = data.voicemails || [];
      console.log(results);
      setMessages(results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
  const cols = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (index === 0) {
        return (
          <span style={{ fontWeight: 600 }}>
            {new Date(cell).toLocaleString("en-US", {
              timeZone: "America/Los_Angeles",
            })}
          </span>
        );
      } else if (index == 3) {
        return <span style={{ fontWeight: 600 }}>{formatDuration(cell)}</span>;
      } else if (index == 4) {
        return <span style={{ fontWeight: 600 }}>{formatFileSize(cell)}</span>;
      }
      return cell ?? "-";
    },
  }));
  
  cols.push({
    dataField: "actions",
    text: "Actions",
    formatter: (_, row, rowIndex, formatExtraData) => {
      const isPlaying = formatExtraData.currentlyPlaying === row.filename;
      return (
        <>
          <Button
            variant={isPlaying ? "warning" : "success"}
            size="sm"
            onClick={() => handlePlay(row.full_path, row)}
          >
            {isPlaying ? "⏸ Stop" : "▶ Play"}
          </Button>
          <Button variant="primary" size="sm" onClick={() => handleDownload(row)}>
            <i className="fas fa-download"></i> Download
          </Button>
        </>
      );
    },
    formatExtraData: { currentlyPlaying }, 
  });
  
  return cols;
}, [visibleColumns, currentlyPlaying]);

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: "asc", // "asc" | "desc"
    });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

   const filteredRows = React.useMemo(() => {
      if (!search) return messages;
  
      return messages.filter((row) =>
        // AND matches search term
        visibleColumns.some((col) =>
          String(row[col.key] ?? "")
            .toLowerCase()
            .includes(search.toLowerCase()),
        ),
      );
    }, [messages, search, visibleColumns]);

  useEffect(() => {
    fetchMessages();
  }, [id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePlay = (path, row) => {
    console.log(row)
    console.log("Currently playing, ", currentlyPlaying)
    if (currentlyPlaying === row.filename) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentlyPlaying(null);
      return;
    }
    setCurrentlyPlaying(row.filename);
    console.log("Set to:", row.filename);
    audioRef.current.src = `${serverHost}/play_voicemail/${encodeURIComponent(
      path,
    )}`;
    audioRef.current.play();
  };

  useEffect(() => {
    console.log("currentlyPlaying changed:", currentlyPlaying);
  }, [currentlyPlaying]);

  const handleDownload = async (msg) => {
    console.log(msg);
    const cleanPath = msg.full_path.replace(/^\/+/, ''); // strip leading slashes
    const url = `${serverHost}/download/${cleanPath}`;
    await fetch(url)
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "voicemail.wav";
        a.click();
      });
  };

  const handleAudioEnded = () => {

    setCurrentlyPlaying(null);
  };

  const goToSettings = () => {
    navigate(`/admin/hucs/edit/voicemail/${id}`);
  };

  const handleBack = () => {
    navigate(`/admin/hucs/voicemails`);
  };

  // NEW: fires when a single row's checkbox is toggled
  const handleSelectRow = (row, isSelect) => {
    setSelectedRows((prev) =>
      isSelect
        ? [...prev, row.full_path]
        : prev.filter((path) => path !== row.full_path),
    );
  };

  // NEW: fires when the header checkbox is toggled — `rows` is the currently visible/filtered set
  const handleSelectAllRows = (isSelect, rows) => {
    setSelectedRows(isSelect ? rows.map((r) => r.full_path) : []);
  };

  // NEW: bulk delete for whatever is currently selected
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedRows.length} selected voicemail${
        selectedRows.length > 1 ? "s" : ""
      }? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      // Adjust this endpoint/method to match your actual delete API.
      await Promise.all(
        selectedRows.map((fullPath) => {
          const cleanPath = fullPath.replace(/^\/+/, "");
          return fetch(`${serverHost}/voicemails/${id}/${encodeURIComponent(cleanPath)}`, {
            method: "DELETE",
          });
        }),
      );
      setSelectedRows([]);
      await fetchMessages();
    } catch (err) {
      setError("Failed to delete one or more selected voicemails.");
    } finally {
      setDeleting(false);
    }
  };

  // NEW: react-bootstrap-table2 selectRow config — gives us the checkbox column
  // plus a built-in "select all" checkbox in the header for free.
  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedRows,
    onSelect: handleSelectRow,
    onSelectAll: handleSelectAllRows,
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading voicemail messages...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader
        name="Voicemail Messages"
        parentName="Voicemail"
        padding="4.5rem"
      />

      <Container fluid className="mt--6">
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onTimeUpdate={() => {
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onLoadedMetadata={() => {
            if (audioRef.current) {
              setDuration(audioRef.current.duration);
            }
          }}
        />
        {currentlyPlaying && (
          <Card
            className={`mb-3 ${isDark ? "bg-dark text-light" : ""}`}
            style={{ borderLeft: "4px solid #0d6efd" }}
          >
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>Now Playing:</strong>
                <span>{currentlyPlaying}</span>
              </div>

              <div
                style={{
                  height: "8px",
                  backgroundColor: isDark ? "#374151" : "#e5e7eb",
                  borderRadius: "4px",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  audioRef.current.currentTime = percent * duration;
                }}
              >
                <div
                  style={{
                    width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                    height: "100%",
                    backgroundColor: "#0d6efd",
                    transition: "width 0.1s linear",
                  }}
                />
              </div>

              <div
                className="d-flex justify-content-between mt-1"
                style={{ fontSize: "0.8rem" }}
              >
                <span>{formatDuration(currentTime)}</span>
                <span>{formatDuration(duration)}</span>
              </div>
            </Card.Body>
          </Card>
        )}
        <Card
          className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light" : ""}`}
        >
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center gap-3">
              <Badge bg="info" style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                {messages.length} messages
              </Badge>
              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            <div className="d-flex gap-2">
              {selectedRows.length > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  <i className="fas fa-trash"></i>{" "}
                  {deleting
                    ? "Deleting..."
                    : `Delete (${selectedRows.length})`}
                </Button>
              )}
              <Button variant="info" size="sm" onClick={fetchMessages}>
                <i className="fas fa-sync-alt"></i> Refresh
              </Button>
              <Button variant="light" size="sm" onClick={goToSettings}>
                <i className="fas fa-user-cog"></i> Settings
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ padding: 0 }}>
             <div style={{ overflowX: "auto" }}>
              <BootstrapTable
                keyField={"full_path"}
                data={filteredRows} // <-- use filteredRows instead of all rows
                columns={columns}
                bootstrap4
                pagination={pagination}
                bordered={false}
                hover
                selectRow={selectRow}
                classes="align-items-center table-flush"
                noDataIndication={() =>
                  loading ? (
                    <div className="text-center">Loading...</div>
                  ) : (
                    <div className="text-center">No records found.</div>
                  )
                }
              />
            </div>
          </Card.Body>
        </Card>
        <Button variant="light" onClick={handleBack}>
          Back
        </Button>
      </Container>
    </>
  );
};

export default VoicemailDetail;